import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { FeeRate, Fund } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { parseAmount } from '../../validation'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'

const pct = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const format = (rate: number) => `${pct.format(rate * 100)} %`

/**
 * Předvyplnění editace: přehledová sazba se tiskne na 2 des. místa, ale do
 * formuláře musí jít PŘESNÁ hodnota — jinak uložení jedné sazby potichu zaokrouhlí
 * tu druhou (0,014750 → „1,48" → 0,0148). Zaokrouhlení jen na plovoucí artefakty.
 */
const editValue = (rate: number) => String(Math.round(rate * 1e8) / 1e6).replace('.', ',')
const classLabel = (c: FeeRate['feeClass']) => (c === 'A' ? 'A (EB)' : 'B (NORM)')

interface FeeRatesCardProps {
  feeRates: FeeRate[]
  funds: Fund[]
  canManage: boolean
  onUpdated: () => void
}

/**
 * Správa sazeb fee per fond × třída (F6-A): management fee (p.a.) + success fee.
 * Editace jen backoffice/CEO. A = EB (nižší), B = NORM (vyšší); TREASURY jen A.
 * MF je už v NAV od správce (modul ho nestrhává), SF přímo z klienta na konci roku.
 * Základna provizí řídí provize distributorů z měsíčního běhu MF.
 */
export function FeeRatesCard({ feeRates, funds, canManage, onUpdated }: FeeRatesCardProps) {
  const api = useOpsApi()
  const [editing, setEditing] = useState<FeeRate | null>(null)
  const [mfText, setMfText] = useState('')
  const [sfText, setSfText] = useState('')
  const [exitText, setExitText] = useState('')
  const [baseText, setBaseText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fundName = (key: string) => funds.find((f) => f.key === key)?.name ?? key

  const openEdit = (rate: FeeRate) => {
    setEditing(rate)
    setMfText(editValue(rate.mgmtFeePa))
    setSfText(editValue(rate.successFeeRate))
    setExitText(editValue(rate.exitFeeRate ?? 0))
    setBaseText(editValue(rate.commissionBasePa ?? 0.0147))
    setError(null)
  }

  const handleSave = async () => {
    if (!editing) return
    const mf = parseAmount(mfText)
    const sf = parseAmount(sfText)
    const exit = parseAmount(exitText)
    const base = parseAmount(baseText)
    if (mf === null || mf < 0 || sf === null || sf < 0) {
      setError('Zadejte sazby v procentech (např. 1,47 a 15)')
      return
    }
    if (exit === null || exit < 0 || exit > 100) {
      setError('Výstupní poplatek zadejte v procentech (0 až 100)')
      return
    }
    if (base === null || base < 0 || base > 100) {
      setError('Základnu provizí zadejte v procentech (např. 1,47)')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api.updateFeeRate({
        fundKey: editing.fundKey,
        feeClass: editing.feeClass,
        mgmtFeePa: mf / 100,
        successFeeRate: sf / 100,
        exitFeeRate: exit / 100,
        commissionBasePa: base / 100,
      })
      setEditing(null)
      onUpdated()
    } catch (err) {
      setError(apiError(err, 'Uložení sazby selhalo'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title="Sazby fee (fond × třída)"
      info={
        <InfoHint
          title="Jak sazby fungují"
          lines={[
            'Třída A = EB (Early Birds, nižší sazby), třída B = NORM/Dohoda (vyšší). TREASURY má jednu třídu.',
            'Management fee je sazba ze smlouvy. Strhává ho správce v NAV, modul ho nestrhává podruhé.',
            'Základna provizí (p.a.) slouží k výpočtu provizí distributorů: jednotky × cena ke konci měsíce × základna / 12 × podíl distributora, až od měsíce po vkladu. Zatím 1,47 % všude.',
            'Success fee je sazba z ročního zisku jednotky nad high-water markem (viz záložka Success fee).',
            'Výstupní poplatek se strhává z hrubé hodnoty odkupu jednotek (záložka Vklady / výběry); default 0 %, u konkrétní žádosti ho lze přepsat.',
            'Sazby jsou editovatelné zde — nikde v kódu nejsou natvrdo; změna platí pro další běhy.',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fond</TableCell>
              <TableCell>Třída</TableCell>
              <TableCell align="right">Management fee (p.a.)</TableCell>
              <TableCell align="right">Základna provizí (p.a.)</TableCell>
              <TableCell align="right">Success fee</TableCell>
              <TableCell align="right">Výstupní poplatek</TableCell>
              {canManage && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {feeRates.map((rate) => (
              <TableRow key={`${rate.fundKey}-${rate.feeClass}`}>
                <TableCell>{fundName(rate.fundKey)}</TableCell>
                <TableCell>{classLabel(rate.feeClass)}</TableCell>
                <TableCell align="right">{format(rate.mgmtFeePa)}</TableCell>
                <TableCell align="right">{format(rate.commissionBasePa ?? 0.0147)}</TableCell>
                <TableCell align="right">{format(rate.successFeeRate)}</TableCell>
                <TableCell align="right">{format(rate.exitFeeRate ?? 0)}</TableCell>
                {canManage && (
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(rate)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {!canManage && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Sazby upravuje backoffice nebo CEO.
        </Typography>
      )}

      <Dialog open={Boolean(editing)} onClose={saving ? undefined : () => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Sazby — {editing ? `${fundName(editing.fundKey)} ${classLabel(editing.feeClass)}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label="Management fee p.a. (%)"
              value={mfText}
              onChange={(e) => setMfText(e.target.value)}
              placeholder="1,47"
              disabled={saving}
            />
            <TextField
              size="small"
              label="Základna provizí z MF p.a. (%)"
              value={baseText}
              onChange={(e) => setBaseText(e.target.value)}
              placeholder="1,47"
              helperText="Z ní se počítají provize distributorů (jako Excel distributorů)."
              disabled={saving}
            />
            <TextField
              size="small"
              label="Success fee (%)"
              value={sfText}
              onChange={(e) => setSfText(e.target.value)}
              placeholder="15"
              disabled={saving}
            />
            <TextField
              size="small"
              label="Výstupní poplatek (%)"
              value={exitText}
              onChange={(e) => setExitText(e.target.value)}
              placeholder="0"
              helperText="Strhává se z hrubé hodnoty odkupu; default 0 %."
              disabled={saving}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" color="inherit" onClick={() => setEditing(null)} disabled={saving}>
            Zrušit
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Uložit
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  )
}
